/* No Admin API. No cart mutation before an explicit shopper click. */
(() => {
  const E = window.IDCPacks;
  if (!E) return;
  let configuration = null;
  let cartRequest = null;
  let writing = false;
  let sequence = 0;
  const esc = text => String(text ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const jsonRequest = async (url, body) => {
    const response = await fetch(url, { method: body ? 'POST' : 'GET', credentials: 'same-origin', headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) }, ...(body ? { body: JSON.stringify(body) } : {}) });
    if (!response.ok) throw new Error('network');
    return response.json();
  };
  const endpoint = file => `${configuration.root || '/'}cart/${file}.js`;
  const getCart = () => {
    if (!cartRequest) cartRequest = jsonRequest(`${configuration.root || '/'}cart.js`).finally(() => { cartRequest = null; });
    return cartRequest;
  };
  function loadConfig() {
    const source = document.querySelector('[data-idc-config]');
    try {
      configuration = source ? JSON.parse(source.textContent) : null;
      if (configuration) {
        configuration.products = configuration.products.filter((p, i, all) => p.variants.length && all.findIndex(x => x.id === p.id) === i);
        configuration.offers = configuration.offers.filter(o => Number.isInteger(o.settings.quantity) && o.settings.quantity >= 1 && o.settings.quantity <= 6).sort((a, b) => a.settings.quantity - b.settings.quantity);
      }
    } catch (_) { configuration = null; }
    document.querySelectorAll('idc-pack').forEach(el => el.setup());
    document.querySelectorAll('idc-cart-assurance, idc-pack-label').forEach(el => el.paint());
  }
  class Pack extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.uid = `idc-pack-${++sequence}`;
      this.onChange = event => this.change(event);
      this.onClick = event => {
        if (event.target.closest('[data-pack-add]')) this.add();
        const pill = event.target.closest('[data-pack-pill]');
        if (pill && !pill.disabled) {
          const index = pill.dataset.slot, value = pill.dataset.packPill;
          const select = this.querySelector(`[data-pack-variant="${index}"]`);
          select.value = value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          this.querySelector(`[data-pack-pill="${value}"][data-slot="${index}"]`)?.focus();
        }
      };
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => { if (!writing && this.isCart) this.refresh(); });
      this.setup();
    }
    disconnectedCallback() {
      this.unsubscribe?.();
      if (this.dataset.inline === 'true') this.closest('product-info')?.classList.remove('idc-pack-active');
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      this.initialized = false;
    }
    setup() {
      this.config = configuration;
      this.isCart = this.dataset.mode === 'cart';
      this.pdpActive = this.dataset.inline === 'true' && !!this.config?.enabled && !!this.config?.offers.length && this.config.products.some(p => String(p.id) === this.dataset.productId);
      if (this.dataset.inline === 'true') {
        this.closest('product-info')?.classList.toggle('idc-pack-active', this.pdpActive);
        const holder = this.closest('.idc-inline-block');
        if (holder) holder.hidden = !this.config?.enabled || (!!this.config?.products.length && !this.pdpActive);
      }
      this.hidden = !this.config?.enabled || (!!this.dataset.compact && !this.config.drawer);
      if (this.hidden) return;
      this.s = this.config.settings;
      this.currency = this.config.presentment || this.config.currency;
      this.cart = null;
      this.selected = null;
      this.slots = [];
      if (this.isCart) this.refresh(); else this.render();
    }
    money(value) { return new Intl.NumberFormat(this.currency === 'MXN' ? 'es-MX' : (document.documentElement.lang || 'es-MX'), { style: 'currency', currency: this.currency }).format(value / 100); }
    offerPreview(offer) {
      const prices = this.config.products.flatMap(p => p.variants.filter(v => v.available).map(v => v.price));
      if (!prices.length) return '';
      const context = this.isCart ? this.context() : { quantity: 0, subtotal: 0 };
      const subtotal = context.subtotal + (offer.settings.quantity - context.quantity) * Math.min(...prices);
      return `<small>${esc(this.s.from_price)}</small><b>${esc(this.money(E.quote(offer, subtotal, this.config).total))}</b>`;
    }
    async refresh() {
      this.loading = true;
      this.render();
      try { this.cart = await getCart(); this.readError = false; }
      catch (_) { this.readError = true; }
      this.loading = false;
      if (this.isConnected) this.render();
    }
    context() { return E.eligible(this.cart || { items: [] }, this.config.products); }
    availableOffers() {
      const count = this.isCart ? this.context().quantity : 0;
      return this.config.offers.filter(o => o.settings.quantity > count);
    }
    currentOffer() {
      const offers = this.availableOffers();
      return offers.find(o => o.id === this.selected) || (this.isCart ? offers[0] : offers.find(o => o.settings.quantity === 2)) || offers[0];
    }
    fillSlots(count) {
      while (this.slots.length < count) this.slots.push({ product: '', variant: '' });
      this.slots.length = count;
      // Product page may preselect its model; size/color remains a deliberate choice.
      if (this.dataset.productId && this.slots.length && !this.slots[0].product && !this.isCart) {
        const p = this.config.products.find(p => String(p.id) === this.dataset.productId);
        if (p) { this.slots[0].product = String(p.id); this.slots[0].variant = p.variants.length === 1 && p.variants[0].available ? String(p.variants[0].id) : ''; }
      }
    }
    slotMarkup(slot, i) {
      const s = this.s;
      const p = this.config.products.find(p => String(p.id) === slot.product);
      const v = p?.variants.find(v => String(v.id) === slot.variant);
      const img = v?.image || p?.image;
      return `<div class="idc-pack__slot"><div class="idc-pack__thumb">${img ? `<img src="${esc(img)}" alt="${esc(p.title)}" width="88" height="100" loading="lazy">` : `<span aria-hidden="true">${String(i + 1).padStart(2, '0')}</span>`}</div><div class="idc-pack__fields"><label for="${this.uid}-product-${i}">${esc(s.piece)} ${i + 1}</label><select id="${this.uid}-product-${i}" data-pack-product="${i}"><option value="">${esc(s.choose)}</option>${this.config.products.map(item => `<option value="${item.id}" ${String(item.id) === slot.product ? 'selected' : ''} ${!item.variants.some(v => v.available) ? 'disabled' : ''}>${esc(item.title)}${!item.variants.some(v => v.available) ? ` — ${esc(s.unavailable)}` : ''}</option>`).join('')}</select><label class="visually-hidden" for="${this.uid}-variant-${i}">${esc(s.variant)} ${i + 1}</label><select id="${this.uid}-variant-${i}" data-pack-variant="${i}" ${!p ? 'disabled' : ''}><option value="">${esc(s.variant)}</option>${(p?.variants || []).map(v => `<option value="${v.id}" ${String(v.id) === slot.variant ? 'selected' : ''} ${!v.available ? 'disabled' : ''}>${esc(v.title)}${!v.available ? ` — ${esc(s.unavailable)}` : ''}</option>`).join('')}</select></div></div>`;
    }
    render() {
      if (!this.config?.enabled) return;
      const root = this.querySelector('[data-idc-pack-root]') || this;
      const s = this.s;
      if (this.isCart && this.loading) { root.innerHTML = `<div class="idc-pack__loading" role="status" aria-busy="true">${esc(s.cart_title)}</div>`; return; }
      if (this.isCart && this.readError) { root.innerHTML = `<p role="status">${esc(s.error)}</p>`; return; }
      const title = this.isCart ? `${this.dataset.compact ? '<idc-promo data-layout="inline" hidden></idc-promo>' : ''}<div class="idc-pack__head"><h2>${esc(s.cart_title)}</h2><p>${esc(s.cart_intro)}</p></div>` : '';
      if (!this.config.products.length || !this.config.offers.length) {
        root.innerHTML = `${title}<div class="idc-pack__empty"><div class="idc-pack__empty-numbers" aria-hidden="true"><span>01</span><span>02</span><span>03</span></div><p>${esc(s.empty)}</p></div>`;
        return;
      }
      const offer = this.currentOffer();
      const context = this.context();
      if (!offer) { root.innerHTML = `${title}<p class="idc-pack__done">${esc(s.all_done)}</p>`; return; }
      this.selected = offer.id;
      this.fillSlots(offer.settings.quantity - (this.isCart ? context.quantity : 0));
      root.innerHTML = `${title}<div class="idc-pack__panel">
        ${this.isCart ? `<div class="idc-pack__progress"><span>${esc(s.added)} <b>${context.quantity}</b></span><span>${esc(s.missing)} <b>${this.slots.length}</b></span><progress max="${offer.settings.quantity}" value="${context.quantity}" aria-label="${esc(s.added)}"></progress></div>` : ''}
        <fieldset class="idc-pack__offers"><legend class="visually-hidden">${esc(s.choose)}</legend>${this.availableOffers().map(o => `<label class="idc-pack__offer ${o.id === offer.id ? 'is-selected' : ''}"><input type="radio" name="${this.uid}-offer" data-pack-offer value="${esc(o.id)}" ${o.id === offer.id ? 'checked' : ''}><span><strong>${esc(o.settings.title)}</strong><small>${o.settings.quantity} ${esc(o.settings.quantity === 1 ? s.piece : s.pieces).toLowerCase()}${o.settings.description ? ` · ${esc(o.settings.description)}` : ''}</small>${o.settings.badge ? `<em>${esc(o.settings.badge)}</em>` : ''}</span><span class="idc-pack__offer-price">${this.offerPreview(o)}</span></label>`).join('')}</fieldset>
        <div class="idc-pack__slots">${this.slots.map((slot, i) => this.slotMarkup(slot, i)).join('')}</div>
        <div class="idc-pack__price" data-pack-price aria-live="polite"></div>
        <button type="button" class="idc__button idc-pack__add" data-pack-add>${esc(this.isCart ? s.add_missing : s.add)}</button>
        <p class="idc-pack__notice">${esc(s.notice)}</p>
        <p class="idc-pack__feedback" role="status" aria-live="polite" data-pack-feedback></p>
        <a href="${esc(this.config.cartUrl)}" class="idc-pack__review" data-pack-review hidden>${esc(s.review)}</a>
      </div>`;
      this.updatePrice();
    }
    change(event) {
      const node = event.target;
      const focusId = node.id;
      if (node.matches('[data-pack-offer]')) { this.selected = node.value; }
      if (node.matches('[data-pack-product]')) {
        const index = Number(node.dataset.packProduct);
        const p = this.config.products.find(p => String(p.id) === node.value);
        this.slots[index] = { product: node.value, variant: p?.variants.length === 1 && p.variants[0].available ? String(p.variants[0].id) : '' };
      }
      if (node.matches('[data-pack-variant]')) this.slots[Number(node.dataset.packVariant)].variant = node.value;
      this.render();
      this.renderVariantPills();
      if (focusId) this.querySelector(`[id="${focusId}"]`)?.focus();
      else this.querySelector(`input[data-pack-offer]:checked`)?.focus();
    }
    updatePrice() {
      this.renderVariantPills();
      const prices = this.slots.map(slot => this.config.products.find(p => String(p.id) === slot.product)?.variants.find(v => String(v.id) === slot.variant)?.price);
      this.complete = prices.every(p => Number.isFinite(p));
      const el = this.querySelector('[data-pack-price]');
      if (!el) return;
      if (!this.complete) { el.innerHTML = `<span>${esc(this.s.select_error)}</span>`; return; }
      const subtotal = prices.reduce((a, b) => a + b, this.isCart ? this.context().subtotal : 0);
      const q = E.quote(this.currentOffer(), subtotal, this.config);
      el.innerHTML = `<span>${esc(q.promotional ? this.s.estimate : this.s.regular)}</span><strong>${esc(this.money(q.total))}</strong>${q.savings ? `<small><s>${esc(this.money(subtotal))}</s> · ${esc(this.s.save)} ${esc(this.money(q.savings))}</small>` : ''}`;
    }
    renderVariantPills() {
      if (!this.s?.variant_buttons) return;
      this.querySelectorAll('[data-pack-variant]').forEach(select => {
        if (select.disabled || select.options.length > 21 || select.parentElement.querySelector('.idc-pack__variant-pills')) return;
        const group = document.createElement('div');
        group.className = 'idc-pack__variant-pills';
        group.setAttribute('role', 'group');
        group.setAttribute('aria-label', `${this.s.variant} ${Number(select.dataset.packVariant) + 1}`);
        group.innerHTML = [...select.options].filter(o => o.value).map(o => `<button type="button" data-pack-pill="${esc(o.value)}" data-slot="${select.dataset.packVariant}" aria-pressed="${o.selected}" ${o.disabled ? 'disabled' : ''}>${esc(o.textContent)}</button>`).join('');
        select.after(group);select.hidden = true;select.tabIndex = -1;
      });
    }
    feedback(text, review = false) {
      const message = this.querySelector('[data-pack-feedback]');
      if (message) message.textContent = text;
      const link = this.querySelector('[data-pack-review]');
      if (link) link.hidden = !review;
    }
    async add() {
      if (writing) return;
      if (!this.complete) { this.feedback(this.s.select_error); return; }
      writing = true;
      const button = this.querySelector('[data-pack-add]');
      button.disabled = true;
      button.textContent = this.s.busy;
      this.setAttribute('aria-busy', 'true');
      // Freeze choices during the request to prevent a mixed selection/discount.
      this.querySelectorAll('select, input').forEach(el => { el.disabled = true; });
      const offer = this.currentOffer();
      const selections = this.slots.map(slot => slot.variant);
      try {
        const before = await getCart();
        if (this.isCart && E.eligible(before, this.config.products).quantity !== this.context().quantity) throw new Error('cart_changed');
        const items = E.aggregate(selections, this.config.products, before);
        if (!items.length) throw new Error('selection');
        await jsonRequest(endpoint('add'), { items });
        if (E.promotion(offer, this.config) && offer.settings.method === 'code') {
          const current = await getCart();
          const result = await jsonRequest(endpoint('update'), { discount: E.codes(current, offer.settings.code) });
          const applied = (result.discount_codes || []).some(c => c.code.toLowerCase() === offer.settings.code.trim().toLowerCase() && c.applicable);
          if (!applied) throw new Error('discount');
        }
        this.feedback(this.s.ready, true);
        window.location.assign(this.config.cartUrl);
      } catch (error) {
        // Never retry a POST: a network error can happen after Shopify accepted items.
        this.feedback(error.message === 'discount' ? this.s.discount_error : this.s.error, true);
        button.hidden = true;
        this.querySelector('[data-pack-review]')?.focus();
      } finally {
        writing = false;
        this.removeAttribute('aria-busy');
      }
    }
  }
  class Assurance extends HTMLElement {
    connectedCallback() { this.paint(); }
    paint() {
      if (!configuration) return;
      this.innerHTML = `<p>${esc(configuration.settings.cart_shipping)}</p><small>${esc(configuration.settings.cart_secure)}</small>`;
    }
  }
  class PackLabel extends HTMLElement {
    connectedCallback() { this.paint(); }
    paint() {
      if (!configuration) return;
      this.textContent = configuration.settings.cart_title;
      const fold = this.closest('.idc-pack-fold');
      if (fold) {
        fold.hidden = !configuration.enabled || !configuration.drawer;
        fold.open = configuration.settings.drawer_open !== false;
      }
    }
  }
  customElements.define('idc-cart-assurance', Assurance);
  customElements.define('idc-pack-label', PackLabel);
  customElements.define('idc-pack', Pack);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', loadConfig); else loadConfig();
  document.addEventListener('shopify:section:load', loadConfig);
  document.addEventListener('shopify:section:unload', () => requestAnimationFrame(loadConfig));
})();
